"use client";
import { MailIcon } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

export default function EnterpriseContactForm() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // In a real implementation, this would send to your enterprise contact endpoint
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success("Thank you for your inquiry. Our team will contact you soon.");
      
      // Reset form
      setEmail("");
      setName("");
      setCompany("");
      setMessage("");
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 dark:text-gray-100">
      <div className="container mx-auto space-y-8 px-4 py-16 lg:px-8 lg:py-32 xl:max-w-7xl">
        <div className="text-center">
          <MailIcon className="mb-5 inline-block h-12 w-12 text-blue-600 dark:text-blue-500" />
          <h2 className="mb-4 text-4xl font-black text-blue-700 dark:text-white inline-block">
            Contact Our Enterprise Team
          </h2>
          <h3 className="mx-auto text-xl font-medium leading-relaxed text-gray-700 dark:text-gray-300 lg:w-2/3">
            Get in touch with our team to learn how EvidenceSync can transform your organization's evidence planning processes.
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="mx-auto space-y-6 lg:max-w-2xl">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Full Name *
              </label>
              <Input
                type="text"
                id="name"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="John Doe"
              />
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Work Email *
              </label>
              <Input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="john.doe@company.com"
              />
            </div>
          </div>

          <div>
            <label htmlFor="company" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Company *
            </label>
            <Input
              type="text"
              id="company"
              name="company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              required
              placeholder="Your Company Name"
            />
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Message
            </label>
            <Textarea
              id="message"
              name="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              placeholder="Tell us about your evidence planning needs..."
            />
          </div>

          <div className="text-center">
            <Button
              type="submit"
              className="inline-flex items-center justify-center space-x-2 rounded-lg border border-blue-700 bg-blue-700 px-8 py-3 font-semibold leading-6 text-white hover:border-blue-600 hover:bg-blue-600 hover:text-white focus:ring focus:ring-blue-400 focus:ring-opacity-50 active:border-blue-700 active:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting || !email || !name || !company}
            >
              {isSubmitting ? "Sending..." : "Contact Us"}
            </Button>
          </div>

          <p className="text-sm text-center text-gray-600 dark:text-gray-400">
            * Required fields. For immediate assistance, please contact your system administrator.
          </p>
        </form>
      </div>
    </div>
  );
}